<script setup lang="ts">
import { onMounted, onUnmounted, computed, useId } from "vue";
import { motion, AnimatePresence } from "motion-v";
import IconClose from "~icons/ic/round-close";
import { EModalPosition } from "@/services/application/interfaces";
import { ModalPosition } from "@/services/application/constantes";

const modalId = useId();

const props = withDefaults(
  defineProps<{
    disableBackDrop?: boolean;
    haveHeader?: boolean;
    position?: keyof typeof EModalPosition;
    disableClose?: boolean;
    focus?: boolean;
    /**
     * Opaque panel and no blur in the entrance animation. Blurring over a page full of
     * charts drops frames while the modal opens; pages that open drill-downs often opt out.
     */
    noBlur?: boolean;
    /**
     * Sit beside another open modal: no dim overlay, clicks pass through the wrapper.
     */
    attached?: boolean;
    attachedAlign?: 'top' | 'bottom';
  }>(),
  {
    position: EModalPosition.base,
    haveHeader: true,
    disableBackDrop: false,
    disableClose: false,
    focus: false,
    noBlur: false,
    attached: false,
    attachedAlign: 'bottom',
  },
);

const emit = defineEmits(["update:modelValue"]);

const isVisible = defineModel<boolean>();

type closeSource = "unknow" | "backdrop" | "closeIcon";

const toggleModal = (source: closeSource = "unknow") => {
  if (source === "backdrop" && props.disableBackDrop) return;
  isVisible.value = !isVisible.value;
};

const handleKeydown = (e: KeyboardEvent) => {
  // Only close if Escape wasn't already handled by another component (e.g., Combobox dropdown)
  if (e.key === "Escape" && isVisible.value && !props.disableClose && !e.defaultPrevented) {
    toggleModal("closeIcon");
    e.stopPropagation();
    if (props.attached) e.stopImmediatePropagation();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown, props.attached);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown, props.attached);
  // Force-close so Teleport + AnimatePresence cannot leave a stuck overlay
  // when the parent unmounts mid-exit (e.g. confirm → navigate).
  if (isVisible.value) {
    isVisible.value = false;
  }
  // AnimatePresence may keep the exiting node after Vue has already unmounted
  // the Teleport. Remove any leftover overlay that still belongs to this instance.
  document
    .querySelectorAll(`[data-b-modal-id="${CSS.escape(modalId)}"]`)
    .forEach((el) => el.remove());
});

// Animation variants for different modal positions
// Using full transform strings for hardware acceleration (individual x/y use CSS variables which aren't GPU accelerated)
const modalAnimations = {
  base: {
    initial: { opacity: 0, transform: "scale(0.95)", filter: "blur(8px)" },
    animate: { opacity: 1, transform: "scale(1)", filter: "blur(0px)" },
    exit: { opacity: 0, transform: "scale(0.92)", filter: "blur(8px)" },
    transition: { duration: 0.15, ease: [0.2, 0, 0, 1] },
  },
  bottom: {
    initial: { transform: "translateY(100%)" },
    animate: { transform: "translateY(0%)" },
    exit: { transform: "translateY(100%)" },
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  },
  top: {
    initial: { transform: "translateY(-100%)" },
    animate: { transform: "translateY(0%)" },
    exit: { transform: "translateY(-100%)" },
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  },
  topFull: {
    initial: { transform: "translateY(-100%)" },
    animate: { transform: "translateY(0%)" },
    exit: { transform: "translateY(-100%)" },
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  },
  right: {
    initial: { transform: "translateX(100%)" },
    animate: { transform: "translateX(0%)" },
    exit: { transform: "translateX(100%)" },
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  },
  left: {
    initial: { transform: "translateX(-100%)" },
    animate: { transform: "translateX(0%)" },
    exit: { transform: "translateX(-100%)" },
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  },
};

const withoutFilter = (state: Record<string, any>) => {
  const { filter, ...rest } = state;
  return rest;
};

const currentAnimation = computed(() => {
  const animation = modalAnimations[props.position] || modalAnimations.base;
  if (!props.noBlur) return animation;
  return {
    ...animation,
    initial: withoutFilter(animation.initial),
    animate: withoutFilter(animation.animate),
    exit: withoutFilter(animation.exit),
  };
});

const surfaceClass = computed(() => (props.noBlur
  ? "bg-white dark:bg-[#141E28]"
  : "bg-white/85 dark:bg-[#141E28]/85 backdrop-blur-xl backdrop-saturate-150"));

const containerTransition = computed(() => currentAnimation.value.transition);

const containerClass = computed(() => [
  ModalPosition[props.position],
  props.focus
    ? "modal-container-focus fixed inset-0 h-dvh max-h-dvh w-dvw max-w-dvw rounded-none border-0"
    : "",
  props.attached && props.attachedAlign === "top" && "attached-align-top",
  props.attached && props.attachedAlign === "bottom" && "attached-align-bottom",
]);

const headerClass = computed(() =>
  props.focus
    ? "flex items-center justify-between px-4 py-3 shadow-sm shrink-0"
    : "flex items-center justify-between p-3 shadow-sm shrink-0",
);

const mainClass = computed(() =>
  props.focus
    ? "flex h-full flex-col grow min-h-0 p-0"
    : ["p-2 lg:p-5 flex flex-col", props.attached ? "grow-0" : "grow"],
);

const footerClass = computed(() =>
  props.focus
    ? "px-4 py-3 flex justify-end flex-wrap gap-4 shrink-0"
    : "p-3 flex justify-end flex-wrap gap-4 shrink-0",
);
</script>

<script lang="ts">
// use normal <script> to declare options
export default {
  inheritAttrs: false,
};
</script>

<template>
  <Teleport to="body">
    <!-- mode="wait" uses Vue Transition (not TransitionGroup), which avoids
         motion-v's onMotionExitComplete crash with nested motion children. -->
    <AnimatePresence mode="wait">
      <motion.div
        v-if="isVisible"
        :data-b-modal-id="modalId"
        key="modal-wrapper"
        class="modal"
        :class="{ 'pointer-events-none modal-attached': attached }"
        :initial="{ opacity: 0 }"
        :animate="{ opacity: 1 }"
        :exit="{ opacity: 0 }"
        :transition="{ duration: 0.15 }"
      >
        <div
          class="backdrop"
          :class="{ 'pointer-events-none': attached }"
          @click="toggleModal('backdrop')"
        />
        <motion.div
          key="modal-content"
          class="modal-container border border-white/20 dark:border-white/10 rounded-sm flex flex-col"
          v-bind="$attrs"
          :class="[surfaceClass, containerClass, attached && 'modal-container-attached', $attrs.class]"
          :initial="currentAnimation.initial"
          :animate="currentAnimation.animate"
          :transition="containerTransition"
        >
          <header v-if="haveHeader" :class="headerClass">
            <slot name="header" />
            <IconClose
              v-if="!disableClose"
              class="ml-auto cursor-pointer hover:text-primary-500"
              @click="toggleModal('closeIcon')"
            />
          </header>
          <main :class="mainClass">
            <slot />
          </main>
          <footer v-if="$slots.footer" :class="footerClass">
            <slot name="footer" />
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  </Teleport>
</template>

<style scoped>
.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100dvh;
  width: 100dvw;
  position: fixed;
  /* Above drawer (50) and floating chat widget (60) */
  z-index: 70;
  top: 0;
  left: 0;
}

.backdrop {
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: fixed;
  background-color: transparent;
  will-change: opacity;
}

.modal-container {
  /* Avoid layout-triggering transitions (width/height) - use transform for size changes */
  will-change: transform, opacity;
  box-shadow:
    0 0 0 100vmax rgba(0, 0, 0, 0.3),
    0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-attached {
  z-index: 80;
}

.modal-container-attached {
  pointer-events: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.right-modal.modal-container-attached {
  height: auto;
  max-height: 98dvh;
  --attached-gap: 0.75rem;
  --attached-host-width: min(40rem, 96vw);
}

.right-modal.modal-container-attached.attached-align-top {
  top: 1dvh;
  bottom: auto;
}

.right-modal.modal-container-attached.attached-align-bottom {
  top: auto;
  bottom: 1dvh;
}

@media (min-width: 1280px) {
  .right-modal.modal-container-attached {
    right: calc(1dvw + var(--attached-host-width) + var(--attached-gap));
  }
}

.modal-container-focus {
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.3);
}

.b-modal {
  transform-origin: center;
}

.b-modal main {
  overflow: auto;
}

.right-modal {
  max-width: calc(98dvw - 1dvw);
  display: flex;
  flex-direction: column;
  position: absolute;
  height: 98dvh;
  right: 1dvw;
  border-radius: 15px;
}

.right-modal main {
  max-height: 100%;
  overflow: auto;
}

.left-modal {
  max-width: calc(97dvw);
  display: flex;
  flex-direction: column;
  position: absolute;
  height: 98dvh;
  left: 1dvw;
  border-radius: 15px;
}

.left-modal main {
  height: 100%;
  overflow: auto;
}

.bottom-modal {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 20px 20px 0 0;
  max-width: 100dvw;
}

.bottom-modal main {
  max-height: 70dvh;
  overflow: auto;
  max-width: 100%;
}

.top-modal {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 0 0 20px 20px;
}

.top-modal main {
  max-height: 70dvh;
  overflow: auto;
  max-width: 100%;
}

.top-modal-full {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 0 0 20px 20px;
}

.top-modal-full main {
  max-height: 80dvh;
  overflow: auto;
  max-width: 100%;
}
</style>
